import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { Grid, GridItem } from '../../../components/Grid';
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  MessageBox,
  Button,
  OTPInput,
  TextInput,
} from '../../../components/FormElements';
import API from '../../../api';
import { API_RESPONSE_TYPES } from '../../../constants';
import {
  StyledSpan,
  StyledPara,
  StyledSpanAsButton,
} from '../../../components/Styled';
import { authData } from '../../../utils';

const StyledCallbackUrl = styled(StyledSpan)`
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  margin: 20px 0px 0px 0px;
  padding: 10px;
  line-height: 20px;
  color: #000000;
  background-color: #f3f3f3;
  text-transform: inherit;
  font-style: normal;
`;

const APISettings = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('');
  const [routeStage, setRouteStage] = useState('Request');
  const [oneTimePassword, setOneTimePassword] = useState('');
  const [requestToken, setRequestToken] = useState('');

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus('');
    setResponseMessage('');
  };

  const readData = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get('/auth/api-settings')
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          const { ipAddress, callbackUrl } = data;
          setIpAddress(ipAddress);
          setCallbackUrl(callbackUrl);
          setTimeout(() => {
            resetResponseData();
          }, 3000);
        } else {
          setResponseStatus(status);
          setResponseMessage(message);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.put('/auth/api-settings', {
      ipAddress,
      callbackUrl,
      routeStage,
      oneTimePassword: oneTimePassword ? oneTimePassword.join('') : '',
      requestToken,
    })
      .then((response) => {
        const { status, message, token } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          if (routeStage === 'Request') {
            setRouteStage('OTPVerification');
            setRequestToken(token);
          } else {
            setRouteStage('Request');
            setRequestToken('');
            setOneTimePassword('');
            setTimeout(() => {
              resetResponseData();
            }, 3000);
          }
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClickResendOTP = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    const { authId } = authData.get();
    const formData = {
      requestType: 'API_SETTINGS',
      mobileNumber: authId,
      requestToken,
    };
    API.post('/auth/resend-otp', formData)
      .then((response) => {
        const { status, message, token } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        setRequestToken(token);
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    readData();
  }, [readData]);

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method='POST' action='#' onSubmit={onSubmit}>
          <Grid
            columns={
              'calc(33.33% - 13.33px) calc(33.33% - 13.33px) calc(33.33% - 13.33px)'
            }
          >
            <GridItem>
              <Fieldset>
                <Label required>Server IP</Label>
                <TextInput
                  placeholder='Please enter your server ip'
                  value={ipAddress}
                  onChange={setIpAddress}
                  disabled={routeStage === 'OTPVerification' || isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Grid
            columns={
              'calc(33.33% - 13.33px) calc(33.33% - 13.33px) calc(33.33% - 13.33px)'
            }
          >
            <GridItem>
              <Fieldset>
                <Label required>Callback URL</Label>
                <TextInput
                  placeholder='Please enter your callback url'
                  value={callbackUrl}
                  onChange={setCallbackUrl}
                  disabled={routeStage === 'OTPVerification' || isLoading}
                />
                <StyledCallbackUrl>
                  {`Example : ${
                    callbackUrl
                      ? callbackUrl
                      : `https://www.yourdomain.com/recharge_callback`
                  }?transactionsid=[TRANSACTION_ID]&number=[MOBILE_NUMBER]&amount=[AMOUNT]&commission=[COMMISSION]&operatorsid=[OPERATOR_ID]&status=[STATUS]&refnumber=[YOUR_REF_NUMBER]`}
                </StyledCallbackUrl>
              </Fieldset>
            </GridItem>
          </Grid>
          {routeStage === 'OTPVerification' && (
            <Grid
              columns={
                'calc(33.33% - 13.33px) calc(33.33% - 13.33px) calc(33.33% - 13.33px)'
              }
            >
              <GridItem>
                <StyledPara>
                  Please enter your OTP sent to your mobile number
                </StyledPara>
                <Fieldset>
                  <OTPInput
                    values={oneTimePassword}
                    onChange={setOneTimePassword}
                    length={6}
                    alignInputs='center'
                    disabled={isLoading}
                  />
                </Fieldset>
              </GridItem>
            </Grid>
          )}
          {responseStatus && (
            <Grid
              columns={
                'calc(33.33% - 13.33px) calc(33.33% - 13.33px) calc(33.33% - 13.33px)'
              }
            >
              <GridItem>
                <Fieldset>
                  <MessageBox
                    status={responseStatus}
                    message={responseMessage}
                  />
                </Fieldset>
              </GridItem>
            </Grid>
          )}
          <Fieldset>
            <Button disabled={isLoading === true} type='submit'>
              {routeStage === 'OTPVerification' ? 'Verify & Update' : 'Update'}
            </Button>
            {routeStage === 'OTPVerification' && (
              <StyledSpanAsButton onClick={onClickResendOTP}>
                Resend OTP
              </StyledSpanAsButton>
            )}
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(APISettings);
